import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState, type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes, type ReactNode } from "react";
import { Dialog as RxDialog, Direction as RxDirection, DropdownMenu as RxDropdown, HoverCard as RxHoverCard, Popover as RxPopover, Tooltip as RxTooltip, Tabs as RxTabs, ScrollArea as RxScroll, Separator as RxSeparator, Avatar as RxAvatar, Progress as RxProgress } from "radix-ui";
import { X } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy } from "../lib/microcopy";
import { TONES, toneFor } from "../lib/tone";
import { ActionScope } from "./button";

const closeButton =
  "absolute end-3 top-3 grid size-8 place-items-center rounded-lg text-aeblack-400 transition-colors hover:bg-aeblack-50 hover:text-aeblack-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";

// ---------------------------------------------------------------- Dialog
export const Dialog = RxDialog.Root;
export const DialogTrigger = RxDialog.Trigger;
export const DialogClose = RxDialog.Close;

export const DialogContent = forwardRef<ElementRef<typeof RxDialog.Content>, ComponentPropsWithoutRef<typeof RxDialog.Content> & { size?: "sm" | "md" | "lg" | "xl" | "full"; /** The content places its own close control. */ hideClose?: boolean }>(
  ({ className, children, size = "md", hideClose = false, ...props }, ref) => (
    <RxDialog.Portal>
      <RxDialog.Overlay className="scrim fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
      <RxDialog.Content
        ref={ref}
        className={cn(
          "glass-float @container fixed start-1/2 top-1/2 z-50 grid w-[calc(100%-2rem)] -translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg p-6 duration-300 ease-out-expo data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-[0.97] data-[state=closed]:zoom-out-[0.97] data-[state=open]:slide-in-from-bottom-2",
          size === "sm" && "max-w-md",
          size === "md" && "max-w-lg",
          size === "lg" && "max-w-2xl",
          size === "xl" && "max-w-4xl",
          size === "full" && "max-w-[90vw] h-[85vh]",
          className,
        )}
        {...props}
      >
        <ActionScope emphasis="primary">{children}</ActionScope>
        {hideClose ? null : (
          <RxDialog.Close className={closeButton}>
            <X className="size-4" />
            <span className="sr-only">{microcopy("close")}</span>
          </RxDialog.Close>
        )}
      </RxDialog.Content>
    </RxDialog.Portal>
  ),
);
DialogContent.displayName = "DialogContent";

export const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1.5 text-start", className)} {...props} />
);
export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
);
export const DialogTitle = forwardRef<ElementRef<typeof RxDialog.Title>, ComponentPropsWithoutRef<typeof RxDialog.Title>>(({ className, ...props }, ref) => (
  <RxDialog.Title ref={ref} className={cn("font-heading text-xl font-bold leading-tight text-aeblack-900", className)} {...props} />
));
DialogTitle.displayName = "DialogTitle";
export const DialogDescription = forwardRef<ElementRef<typeof RxDialog.Description>, ComponentPropsWithoutRef<typeof RxDialog.Description>>(({ className, ...props }, ref) => (
  <RxDialog.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = "DialogDescription";

// ---------------------------------------------------------------- Sheet (side drawer)
export const Sheet = RxDialog.Root;
export const SheetTrigger = RxDialog.Trigger;
export const SheetClose = RxDialog.Close;

/**
 * A panel docked to an edge of the window. `emphasis="secondary"` for panels that show a part of the page beside it (the case file,
 * the history) rather than a task of their own.
 */
export const SheetContent = forwardRef<ElementRef<typeof RxDialog.Content>, ComponentPropsWithoutRef<typeof RxDialog.Content> & { side?: "start" | "end" | "bottom"; width?: string; emphasis?: "primary" | "secondary" }>(
  ({ className, children, side = "end", width = "max-w-xl", emphasis = "primary", ...props }, ref) => (
    <RxDialog.Portal>
      <RxDialog.Overlay className="scrim fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
      <RxDialog.Content
        ref={ref}
        className={cn(
          "glass-float @container fixed z-50 flex flex-col gap-4 overflow-hidden transition ease-out-expo data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-200 data-[state=open]:duration-300",
          side === "end" && `inset-y-2 end-2 w-[calc(100%-1rem)] ${width} rounded-lg data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right rtl:data-[state=closed]:slide-out-to-left rtl:data-[state=open]:slide-in-from-left`,
          side === "start" && `inset-y-2 start-2 w-[calc(100%-1rem)] ${width} rounded-lg data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left rtl:data-[state=closed]:slide-out-to-right rtl:data-[state=open]:slide-in-from-right`,
          side === "bottom" && "inset-x-0 bottom-0 max-h-[85vh] rounded-t-3xl border-x-0 border-b-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className,
        )}
        {...props}
      >
        <ActionScope emphasis={emphasis}>{children}</ActionScope>
        <RxDialog.Close className={closeButton}>
          <X className="size-4" />
          <span className="sr-only">{microcopy("close")}</span>
        </RxDialog.Close>
      </RxDialog.Content>
    </RxDialog.Portal>
  ),
);
SheetContent.displayName = "SheetContent";
export const SheetHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-col gap-1.5 p-6 pb-0 text-start", className)} {...props} />;
export const SheetTitle = DialogTitle;
export const SheetDescription = DialogDescription;
export const SheetBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cn("flex-1 overflow-y-auto scrollbar-thin px-6 pb-6", className)} {...props} />;
export const SheetFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-wrap items-center justify-end gap-2 border-t border-aeblack-100 bg-aeblack-50 px-6 py-3", className)} {...props} />;

// Drawer is the same primitive as Sheet under the name some screens use for side panels.
export const Drawer = Sheet;
export const DrawerTrigger = SheetTrigger;
export const DrawerClose = SheetClose;
export const DrawerContent = SheetContent;
export const DrawerHeader = SheetHeader;
export const DrawerTitle = SheetTitle;
export const DrawerDescription = SheetDescription;
export const DrawerBody = SheetBody;
export const DrawerFooter = SheetFooter;

// ---------------------------------------------------------------- Tooltip
export const TooltipProvider = RxTooltip.Provider;

/**
 * The root every app renders its screens in. Radix primitives (tabs, menus, radio groups, sliders) take their reading direction from
 * here, not from the page: without it they render left-to-right inside an Arabic page.
 */
export function UiProvider({ dir, children }: { dir: "rtl" | "ltr"; children: ReactNode }) {
  return (
    <RxDirection.Provider dir={dir}>
      <RxTooltip.Provider>{children}</RxTooltip.Provider>
    </RxDirection.Provider>
  );
}
export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  return (
    <RxTooltip.Root delayDuration={200}>
      <RxTooltip.Trigger asChild>{children}</RxTooltip.Trigger>
      <RxTooltip.Portal>
        <RxTooltip.Content side={side} sideOffset={6} className="z-50 max-w-xs rounded-sm bg-aeblack-900 px-3 py-2 text-xs font-normal text-white shadow-sm animate-in fade-in-0 zoom-in-95">
          {content}
        </RxTooltip.Content>
      </RxTooltip.Portal>
    </RxTooltip.Root>
  );
}

// ---------------------------------------------------------------- Popover
export const Popover = RxPopover.Root;
export const PopoverTrigger = RxPopover.Trigger;
export const PopoverContent = forwardRef<ElementRef<typeof RxPopover.Content>, ComponentPropsWithoutRef<typeof RxPopover.Content>>(({ className, align = "center", sideOffset = 6, ...props }, ref) => (
  <RxPopover.Portal>
    <RxPopover.Content ref={ref} align={align} sideOffset={sideOffset} className={cn("glass-float z-50 w-72 rounded-lg p-4 text-popover-foreground outline-none animate-in fade-in-0 zoom-in-95", className)} {...props} />
  </RxPopover.Portal>
));
PopoverContent.displayName = "PopoverContent";

// ---------------------------------------------------------------- Hover card
/** A preview on hover or keyboard focus (a citation's excerpt, a figure's provenance); the trigger keeps its own click action. */
export function HoverCard({ openDelay = 200, closeDelay = 100, ...props }: ComponentPropsWithoutRef<typeof RxHoverCard.Root>) {
  return <RxHoverCard.Root openDelay={openDelay} closeDelay={closeDelay} {...props} />;
}
export const HoverCardTrigger = RxHoverCard.Trigger;
export const HoverCardContent = forwardRef<ElementRef<typeof RxHoverCard.Content>, ComponentPropsWithoutRef<typeof RxHoverCard.Content>>(({ className, align = "start", sideOffset = 6, collisionPadding = 12, ...props }, ref) => (
  <RxHoverCard.Portal>
    <RxHoverCard.Content ref={ref} align={align} sideOffset={sideOffset} collisionPadding={collisionPadding} className={cn("glass-float z-50 w-80 max-w-[calc(100vw-2rem)] rounded-lg p-3 text-start text-popover-foreground outline-none animate-in fade-in-0 zoom-in-95", className)} {...props} />
  </RxHoverCard.Portal>
));
HoverCardContent.displayName = "HoverCardContent";

// ---------------------------------------------------------------- Dropdown menu
export const DropdownMenu = RxDropdown.Root;
export const DropdownMenuTrigger = RxDropdown.Trigger;
export const DropdownMenuGroup = RxDropdown.Group;
export const DropdownMenuContent = forwardRef<ElementRef<typeof RxDropdown.Content>, ComponentPropsWithoutRef<typeof RxDropdown.Content>>(({ className, sideOffset = 6, ...props }, ref) => (
  <RxDropdown.Portal>
    <RxDropdown.Content ref={ref} sideOffset={sideOffset} className={cn("glass-float z-50 min-w-48 overflow-hidden rounded-lg p-1.5 text-popover-foreground animate-in fade-in-0 zoom-in-95", className)} {...props} />
  </RxDropdown.Portal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";
export const DropdownMenuItem = forwardRef<ElementRef<typeof RxDropdown.Item>, ComponentPropsWithoutRef<typeof RxDropdown.Item> & { destructive?: boolean }>(({ className, destructive, ...props }, ref) => (
  <RxDropdown.Item ref={ref} className={cn("relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-1.5 text-sm outline-none transition-colors focus:bg-surface-hover focus:text-strong data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:text-subtle", destructive && "text-danger-700 focus:bg-danger-50 focus:text-danger-700", className)} {...props} />
));
DropdownMenuItem.displayName = "DropdownMenuItem";
export const DropdownMenuLabel = forwardRef<ElementRef<typeof RxDropdown.Label>, ComponentPropsWithoutRef<typeof RxDropdown.Label>>(({ className, ...props }, ref) => (
  <RxDropdown.Label ref={ref} className={cn("px-2.5 py-1.5 text-xs font-medium text-subtle", className)} {...props} />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";
export const DropdownMenuSeparator = forwardRef<ElementRef<typeof RxDropdown.Separator>, ComponentPropsWithoutRef<typeof RxDropdown.Separator>>(({ className, ...props }, ref) => (
  <RxDropdown.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-hairline", className)} {...props} />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

// ---------------------------------------------------------------- Tabs
// Tabs used as a view switch declare no panels; their triggers must not point aria-controls at panels that never render.
const TabPanels = createContext<{ count: number; register: () => () => void } | null>(null);

export const Tabs = forwardRef<ElementRef<typeof RxTabs.Root>, ComponentPropsWithoutRef<typeof RxTabs.Root>>((props, ref) => {
  const [count, setCount] = useState(0);
  const register = useCallback(() => {
    setCount((c) => c + 1);
    return () => setCount((c) => c - 1);
  }, []);
  const panels = useMemo(() => ({ count, register }), [count, register]);
  return (
    <TabPanels.Provider value={panels}>
      <RxTabs.Root ref={ref} {...props} />
    </TabPanels.Provider>
  );
});
Tabs.displayName = "Tabs";
export const TabsList = forwardRef<ElementRef<typeof RxTabs.List>, ComponentPropsWithoutRef<typeof RxTabs.List> & { variant?: "pill" | "underline" | "glass" }>(({ className, variant = "pill", ...props }, ref) => (
  <RxTabs.List
    ref={ref}
    className={cn(
      variant === "pill" && "scrollbar-none relative inline-flex max-w-full items-center gap-1 overflow-x-auto",
      variant === "underline" && "relative flex items-center gap-1 border-b-2 border-aeblack-100 overflow-x-auto scrollbar-thin",
      variant === "glass" && "scrollbar-none relative flex w-full items-center gap-1 overflow-x-auto rounded-lg border border-aeblack-100 bg-white p-1",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = "TabsList";
export const TabsTrigger = forwardRef<ElementRef<typeof RxTabs.Trigger>, ComponentPropsWithoutRef<typeof RxTabs.Trigger> & { variant?: "pill" | "underline" | "glass" }>(({ className, variant = "pill", ...props }, ref) => {
  const panels = useContext(TabPanels);
  return (
    <RxTabs.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4",
        variant === "pill" && "h-10 shrink-0 rounded-lg border-2 border-transparent px-4 text-[15px] text-aeblack-800 hover:bg-aeblack-50 data-[state=active]:bg-aegold-100 data-[state=active]:text-aegold-900",
        variant === "underline" && "relative -mb-0.5 rounded-t-lg border-b-[3px] border-transparent px-4 py-3.5 text-[15px] text-aeblack-700 hover:border-aegold-500 hover:text-aeblack-950 data-[state=active]:border-aegold-500 data-[state=active]:text-aegold-700",
        variant === "glass" && "h-9 shrink-0 rounded-md px-3.5 text-sm text-aeblack-800 hover:bg-aeblack-50 data-[state=active]:bg-aegold-100 data-[state=active]:text-aegold-900",
        className,
      )}
      {...props}
      {...(panels?.count === 0 ? { "aria-controls": undefined } : null)}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";
export const TabsContent = forwardRef<ElementRef<typeof RxTabs.Content>, ComponentPropsWithoutRef<typeof RxTabs.Content>>(({ className, ...props }, ref) => {
  const register = useContext(TabPanels)?.register;
  useEffect(() => register?.(), [register]);
  return <RxTabs.Content ref={ref} className={cn("mt-4 focus-visible:outline-none data-[state=active]:animate-fade-in", className)} {...props} />;
});
TabsContent.displayName = "TabsContent";

// ---------------------------------------------------------------- Misc
export const ScrollArea = forwardRef<ElementRef<typeof RxScroll.Root>, ComponentPropsWithoutRef<typeof RxScroll.Root>>(({ className, children, ...props }, ref) => (
  <RxScroll.Root ref={ref} className={cn("relative overflow-hidden", className)} {...props}>
    <RxScroll.Viewport className="size-full rounded-[inherit]">{children}</RxScroll.Viewport>
    <RxScroll.Scrollbar orientation="vertical" className="flex touch-none select-none p-0.5 transition-colors w-2">
      <RxScroll.Thumb className="relative flex-1 rounded-full bg-aeblack-950/20" />
    </RxScroll.Scrollbar>
    <RxScroll.Corner />
  </RxScroll.Root>
));
ScrollArea.displayName = "ScrollArea";

export const Separator = forwardRef<ElementRef<typeof RxSeparator.Root>, ComponentPropsWithoutRef<typeof RxSeparator.Root>>(({ className, orientation = "horizontal", decorative = true, ...props }, ref) => (
  <RxSeparator.Root ref={ref} decorative={decorative} orientation={orientation} className={cn("shrink-0 bg-hairline", orientation === "horizontal" ? "h-px w-full" : "h-full w-px", className)} {...props} />
));
Separator.displayName = "Separator";

const HONORIFICS = /^(د\.?|دكتور|م\.|أ\.|Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Eng\.?|H\.?E\.?)$/i;

/** Two initials from the first and last name, without honorifics; Arabic letters are kept apart (ZWNJ) so they do not join into a word. */
export function initialsOf(name: string): string {
  const words = name.split(/\s+/).filter((w) => w && !HONORIFICS.test(w));
  const picked = words.length > 1 ? [words[0], words[words.length - 1]] : words;
  const arabic = /[\u0600-\u06FF]/.test(name);
  const first = (w: string) => (arabic && w.startsWith("ال") && w.length > 3 ? w.slice(2) : w)[0]?.toUpperCase() ?? "";
  return picked.map(first).join(arabic ? "\u200C" : "");
}

export function Avatar({ name, src, className, size = "md" }: { name: string; src?: string | null; className?: string; size?: "sm" | "md" | "lg" }) {
  const initials = initialsOf(name);
  return (
    <RxAvatar.Root className={cn("relative flex shrink-0 overflow-hidden rounded-sm ring-2 ring-white", size === "sm" && "size-7 text-[10px]", size === "md" && "size-9 text-xs", size === "lg" && "size-12 text-sm", className)}>
      {src ? <RxAvatar.Image src={src} alt={name} className="aspect-square size-full object-cover" /> : null}
      <RxAvatar.Fallback delayMs={src ? 300 : 0} className={cn("flex size-full items-center justify-center font-semibold", TONES[toneFor(name)].tile)}>
        {initials}
      </RxAvatar.Fallback>
    </RxAvatar.Root>
  );
}

export function Progress({ value, className, indicatorClassName, tone = "gold" }: { value: number; className?: string; indicatorClassName?: string; tone?: "gold" | "success" | "warning" | "danger" | "info" }) {
  const toneClass = { gold: "bg-aegold-600", success: "bg-aegreen-600", warning: "bg-camel-500", danger: "bg-aered-600", info: "bg-techblue-600" }[tone];
  return (
    <RxProgress.Root value={value} className={cn("relative h-2 w-full overflow-hidden rounded-full bg-aeblack-100", className)}>
      <RxProgress.Indicator className={cn("h-full rounded-full transition-all duration-700 ease-out-expo", toneClass, indicatorClassName)} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </RxProgress.Root>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton h-4 w-full", className)} />;
}
